/**
 * /admin/hn-launch — super-admin config for the HN launch countdown.
 *
 * Set launch_at + hn_url + tagline. Preview state ∈ {none, countdown, launching, launched}.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Rocket, Calendar, CheckCircle2, AlertTriangle, Trash2, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_META = {
  none: { label: 'No launch scheduled', cls: 'bg-slate-800 text-slate-200 border-slate-600/40' },
  scheduled_far: { label: 'Scheduled · >7d away', cls: 'bg-emerald-500/20 text-cyan-200 border-emerald-400/40' },
  countdown: { label: 'Countdown live', cls: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40' },
  launching: { label: '🚀 LIVE on HN', cls: 'bg-emerald-500/30 text-emerald-100 border-emerald-300/50' },
  launched: { label: 'Past launch (>24h)', cls: 'bg-slate-800 text-slate-200 border-slate-600/40' },
};

export default function HNLaunchAdmin() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    launch_at: '',
    hn_url: '',
    tagline: "The FinOps tool engineers don't hate",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/hn-launch/config`);
      if (r.ok) {
        const d = await r.json();
        setCfg(d);
        if (d.launch_at) {
          const iso = new Date(d.launch_at).toISOString().slice(0, 16);
          setForm({
            launch_at: iso,
            hn_url: d.hn_url || '',
            tagline: d.tagline || "The FinOps tool engineers don't hate",
          });
        }
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    if (!form.launch_at) { setMsg({ ok: false, text: 'Pick a launch date/time' }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        launch_at: new Date(form.launch_at).toISOString(),
        tagline: form.tagline,
      };
      if (form.hn_url) payload.hn_url = form.hn_url;
      const r = await fetch(`${API}/api/hn-launch/config`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ ok: true, text: `Saved · state: ${d.state}` });
        refresh();
      } else setMsg({ ok: false, text: d.detail || `HTTP ${r.status}` });
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm('Clear the launch schedule? Countdown widget will disappear from the landing page.')) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/hn-launch/config`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        setMsg({ ok: true, text: 'Cleared' });
        setForm({ launch_at: '', hn_url: '', tagline: "The FinOps tool engineers don't hate" });
        refresh();
      }
    } finally { setBusy(false); }
  };

  const stateMeta = STATE_META[cfg?.state] || STATE_META.none;

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px">
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="hn-launch-admin-page">
      <Helmet><title>HN Launch Config · CARTIEAI</title></Helmet>

      <div className="mb-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center shadow-xl shadow-emerald-500/40 shrink-0">
            <Rocket className="w-7 h-7 text-white drop-shadow-sm" />
          </div>
          <div className="flex-1">
            <Badge className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-mono tracking-widest mb-2">LAUNCH SEQUENCER</Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 drop-shadow-[0_2px_18px_rgba(16,185,129,0.25)]">
              HN <span className="text-emerald-400">Launch</span>
            </h1>
          </div>
        </div>
        <p className="text-lg text-slate-100 max-w-2xl">
          Set your HN launch moment. Within 7 days of launch, the landing page shows a live emerald <span className="text-emerald-400 font-bold">countdown banner</span>. On launch day, it flips to a "LIVE on HN" banner with one-tap Tweet/LinkedIn share buttons.
        </p>
      </div>

      <Card className="surface-hero surface-hero-accent-strip relative overflow-hidden mb-6" data-testid="hn-launch-status-card">
        <CardHeader>
          <CardTitle className="text-base text-slate-900 flex items-center gap-2">
            <Calendar className="w-4 h-4" style={{color:'rgb(5, 150, 105)'}} /> Current state
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 border text-[9px] font-mono tracking-widest ml-1">{stateMeta.label.toUpperCase()}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cfg?.launch_at ? (
            <div className="space-y-1 text-sm">
              <div className="text-slate-600">Launch at: <span className="font-bold font-mono" style={{color:'rgb(5, 150, 105)'}}>{new Date(cfg.launch_at).toLocaleString()}</span></div>
              {cfg.hn_url && <div className="text-slate-600">HN URL: <a href={cfg.hn_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1" style={{color:'rgb(5, 150, 105)'}}>{cfg.hn_url} <ExternalLink className="w-3 h-3" /></a></div>}
              <div className="text-slate-600">Tagline: <span className="text-slate-900 font-semibold">"{cfg.tagline}"</span></div>
              {cfg.seconds_until_launch != null && (
                <div className="text-slate-600 pt-2 border-t border-slate-200 mt-2">Time to launch: <span className="font-mono font-extrabold text-2xl tabular-nums" style={{color:'rgb(5, 150, 105)'}}>{Math.floor(cfg.seconds_until_launch / 86400)}d {Math.floor((cfg.seconds_until_launch % 86400) / 3600)}h {Math.floor((cfg.seconds_until_launch % 3600) / 60)}m</span></div>
              )}
              {cfg.seconds_since_launch != null && (
                <div className="text-slate-600 pt-2 border-t border-slate-200 mt-2">Time since launch: <span className="font-mono font-extrabold text-2xl tabular-nums" style={{color:'rgb(5, 150, 105)'}}>{Math.floor(cfg.seconds_since_launch / 3600)}h {Math.floor((cfg.seconds_since_launch % 3600) / 60)}m</span></div>
              )}
            </div>
          ) : (
            <div className="text-slate-500 text-sm">No launch configured yet.</div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-950 text-slate-100 border border-emerald-500/30" data-testid="hn-launch-form-card">
        <CardHeader>
          <CardTitle className="text-base text-white">Configure launch</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Launch date & time (local timezone, will be stored as UTC)</label>
            <input
              type="datetime-local"
              value={form.launch_at}
              onChange={(e) => setForm({ ...form, launch_at: e.target.value })}
              className="w-full bg-slate-900 border border-emerald-500/20 rounded px-3 py-2 text-sm text-white"
              data-testid="hn-launch-at-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">HN submission URL (filled in on launch day)</label>
            <input
              type="url"
              placeholder="https://news.ycombinator.com/item?id=…"
              value={form.hn_url}
              onChange={(e) => setForm({ ...form, hn_url: e.target.value })}
              className="w-full bg-slate-900 border border-emerald-500/20 rounded px-3 py-2 text-sm text-white"
              data-testid="hn-launch-url-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Tagline (≤140 chars)</label>
            <input
              type="text"
              maxLength={140}
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              className="w-full bg-slate-900 border border-emerald-500/20 rounded px-3 py-2 text-sm text-white"
              data-testid="hn-launch-tagline-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" data-testid="hn-launch-save-btn">
              {busy ? 'Saving…' : 'Save launch config'}
            </Button>
            {cfg?.launch_at && (
              <Button onClick={clear} disabled={busy} variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="hn-launch-clear-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Clear
              </Button>
            )}
          </div>
          {msg && (
            <div className={`text-xs flex items-center gap-1 ${msg.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
              {msg.ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {msg.text}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </div>
  );
}
